/**
 * Caps the number of in-flight requests to a subset of endpoints (CX-3645).
 *
 * The DSM search grid renders one data-fetching hook per tile, so a ~40-tile
 * result page fires ~40 concurrent GETs at the DSM proxy at once. Because the
 * API is served over HTTP/2 the browser's 6-connection-per-host cap does not
 * apply, so the whole burst hits the upstream simultaneously and drives it past
 * its timeout — which the app surfaces as 502s. Gating those requests through a
 * shared semaphore keeps the upstream load bounded (sliding window) while the
 * queue drains: each completed request frees a slot for the next.
 */

/** Endpoints whose requests share the concurrency budget. */
const DSM_URL_PREFIX = "/v0/digital-sample-management/";

/** Max simultaneous DSM proxy requests. Tuned under CX-3645; adjust with care. */
export const DSM_MAX_CONCURRENCY = 8;

export function isConcurrencyLimitedUrl(url?: string): boolean {
  return !!url && url.startsWith(DSM_URL_PREFIX);
}

export class ConcurrencyLimiter {
  private active = 0;
  private readonly waiters: Array<() => void> = [];

  constructor(private readonly max: number) {}

  /**
   * Resolves once a slot is free. If `signal` aborts while queued, the wait is
   * abandoned and the returned promise rejects — the caller must map that to a
   * cancellation so an unmounted consumer does not see it as a real error.
   */
  acquire(signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) {
      return Promise.reject(new DOMException("Aborted", "AbortError"));
    }
    if (this.active < this.max) {
      this.active++;
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const grant = () => {
        signal?.removeEventListener("abort", onAbort);
        this.active++;
        resolve();
      };
      const onAbort = () => {
        const index = this.waiters.indexOf(grant);
        if (index >= 0) {
          this.waiters.splice(index, 1);
        }
        reject(new DOMException("Aborted", "AbortError"));
      };
      this.waiters.push(grant);
      signal?.addEventListener("abort", onAbort, { once: true });
    });
  }

  release(): void {
    this.active = Math.max(0, this.active - 1);
    const next = this.waiters.shift();
    if (next) {
      next();
    }
  }
}

/** Shared across every axios instance so the cap is global, not per-instance. */
export const dsmConcurrencyLimiter = new ConcurrencyLimiter(
  DSM_MAX_CONCURRENCY,
);
